<template>
  <section>
    <div class="guests-page__head" style="margin: 0 0 14px">
      <div>
        <h2 class="guests-page__title" style="font-size: 18px">
          Conti bancari
        </h2>
        <p class="guests-page__subtitle">
          Più conti associati alla società. Quello predefinito è proposto nella
          generazione dei documenti retta.
        </p>
      </div>
      <button class="villa-btn villa-btn--primary" @click="openCreate">
        <i class="fa-solid fa-plus"></i> Nuovo conto
      </button>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>

    <div v-else class="guests-table-wrap">
      <table class="guests-table">
        <thead>
          <tr>
            <th>Etichetta</th>
            <th>Banca</th>
            <th>IBAN</th>
            <th>Intestatario</th>
            <th>Predefinito</th>
            <th>Stato</th>
            <th class="actions-cell">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!accounts.length" class="no-result-row">
            <td colspan="7" class="no-result">
              Nessun conto bancario. Aggiungi il primo conto.
            </td>
          </tr>
          <tr v-for="a in accounts" :key="a.id">
            <td>{{ a.label }}</td>
            <td>{{ a.bankName }}</td>
            <td>{{ a.iban }}</td>
            <td>{{ a.accountHolder }}</td>
            <td>
              <span
                v-if="a.isDefault"
                class="status-badge status-badge--active"
              >
                Predefinito
              </span>
              <button
                v-else-if="a.isActive"
                class="icon-btn"
                title="Imposta come predefinito"
                @click="makeDefault(a)"
              >
                <i class="fa-regular fa-star"></i>
              </button>
              <span v-else>—</span>
            </td>
            <td>
              <span
                :class="[
                  'status-badge',
                  a.isActive
                    ? 'status-badge--active'
                    : 'status-badge--discharged',
                ]"
              >
                {{ a.isActive ? "Attivo" : "Disattivato" }}
              </span>
            </td>
            <td class="actions-cell">
              <button class="icon-btn" title="Modifica" @click="openEdit(a)">
                <i class="fa-solid fa-pen-to-square"></i>
              </button>
              <button
                v-if="a.isActive"
                class="icon-btn icon-btn--danger"
                title="Disattiva"
                @click="askDeactivate(a)"
              >
                <i class="fa-solid fa-ban"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modale crea/modifica conto -->
    <div
      v-if="modal.open"
      class="villa-modal-backdrop"
      @click.self="closeModal"
    >
      <div class="villa-confirm" style="max-width: 520px">
        <h3 class="villa-confirm__title">
          {{
            modal.mode === "edit" ? "Modifica conto" : "Nuovo conto bancario"
          }}
        </h3>
        <div class="form-grid" style="margin-bottom: 16px">
          <div class="form-field form-field--full">
            <label>Etichetta <span class="required">*</span></label>
            <input
              v-model.trim="form.label"
              type="text"
              placeholder="es. BPER — Gielleci"
            />
          </div>
          <div class="form-field">
            <label>Banca <span class="required">*</span></label>
            <input v-model.trim="form.bankName" type="text" />
          </div>
          <div class="form-field">
            <label>Intestatario <span class="required">*</span></label>
            <input v-model.trim="form.accountHolder" type="text" />
          </div>
          <div class="form-field form-field--full">
            <label>IBAN <span class="required">*</span></label>
            <input v-model.trim="form.iban" type="text" />
          </div>
          <div class="form-field">
            <label>BIC / SWIFT</label>
            <input v-model.trim="form.swiftBic" type="text" />
          </div>
          <div class="form-field">
            <label>Note</label>
            <input v-model.trim="form.notes" type="text" />
          </div>
          <div class="form-field form-field--full">
            <label class="billing-skip">
              <input v-model="form.isDefault" type="checkbox" />
              Imposta come conto predefinito
            </label>
          </div>
        </div>
        <div class="villa-confirm__actions">
          <button class="villa-btn villa-btn--ghost" @click="closeModal">
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--primary"
            :disabled="modal.busy"
            @click="submit"
          >
            <i v-if="modal.busy" class="fa-solid fa-spinner fa-spin"></i>
            {{ modal.mode === "edit" ? "Salva" : "Crea conto" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Conferma disattivazione -->
    <div
      v-if="confirm.open"
      class="villa-modal-backdrop"
      @click.self="confirm.open = false"
    >
      <div class="villa-confirm">
        <h3 class="villa-confirm__title">Disattiva conto</h3>
        <p class="villa-confirm__text">
          Vuoi disattivare <strong>{{ confirm.label }}</strong
          >? Resterà nei documenti già emessi ma non sarà più selezionabile.
        </p>
        <div class="villa-confirm__actions">
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="confirm.busy"
            @click="confirm.open = false"
          >
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--danger"
            :disabled="confirm.busy"
            @click="confirmDeactivate"
          >
            Disattiva
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions } from "vuex";

const emptyForm = () => ({
  label: "",
  bankName: "",
  iban: "",
  accountHolder: "",
  swiftBic: "",
  notes: "",
  isDefault: false,
});

export default {
  name: "CompanyBankAccountsPanel",
  data() {
    return {
      accounts: [],
      loading: true,
      form: emptyForm(),
      modal: { open: false, mode: "create", busy: false, id: null },
      confirm: { open: false, busy: false, id: null, label: "" },
    };
  },
  methods: {
    ...mapActions([
      "fetchBankAccounts",
      "createBankAccount",
      "updateBankAccount",
      "setDefaultBankAccount",
      "deactivateBankAccount",
    ]),
    async load() {
      this.loading = true;
      try {
        this.accounts = await this.fetchBankAccounts({});
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento conti non riuscito."
        );
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.form = emptyForm();
      this.modal = { open: true, mode: "create", busy: false, id: null };
    },
    openEdit(a) {
      this.form = {
        label: a.label || "",
        bankName: a.bankName || "",
        iban: a.iban || "",
        accountHolder: a.accountHolder || "",
        swiftBic: a.swiftBic || "",
        notes: a.notes || "",
        isDefault: !!a.isDefault,
      };
      this.modal = { open: true, mode: "edit", busy: false, id: a.id };
    },
    closeModal() {
      this.modal.open = false;
    },
    async submit() {
      if (
        !this.form.label ||
        !this.form.bankName ||
        !this.form.iban ||
        !this.form.accountHolder
      ) {
        return this.$toast.error(
          "Compila etichetta, banca, IBAN e intestatario."
        );
      }
      this.modal.busy = true;
      try {
        if (this.modal.mode === "edit") {
          await this.updateBankAccount({
            id: this.modal.id,
            payload: { ...this.form },
          });
          this.$toast.success("Conto aggiornato.");
        } else {
          await this.createBankAccount({ ...this.form });
          this.$toast.success("Conto creato.");
        }
        this.modal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Salvataggio non riuscito."
        );
      } finally {
        this.modal.busy = false;
      }
    },
    async makeDefault(a) {
      try {
        await this.setDefaultBankAccount(a.id);
        this.$toast.success(`"${a.label}" è ora il conto predefinito.`);
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Operazione non riuscita."
        );
      }
    },
    askDeactivate(a) {
      this.confirm = { open: true, busy: false, id: a.id, label: a.label };
    },
    async confirmDeactivate() {
      this.confirm.busy = true;
      try {
        await this.deactivateBankAccount(this.confirm.id);
        this.$toast.success("Conto disattivato.");
        this.confirm.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Disattivazione non riuscita."
        );
      } finally {
        this.confirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style scoped>
.villa-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 43, 87, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}
</style>
